import { z } from 'zod';

const trackImportLevelSchema = z.object({
  ordinal: z.number().int().min(1),
  description: z.string().optional(),
  importance: z.enum(['mandatory', 'recommended']),
  checkIds: z.array(z.string().trim().min(1)),
});

/** Strict authored-track contract shared by import previews and mutations. */
export const trackImportDefinitionSchema = z
  .object({
    id: z
      .string()
      .trim()
      .regex(/^[a-z](?:[a-z0-9-]*[a-z0-9])?$/),
    ownerEntityRef: z.string().trim().min(1),
    name: z.string().trim().min(1),
    description: z.string().optional(),
    documentationUrl: z
      .string()
      .trim()
      .refine(value => {
        if (!value) {
          return true;
        }
        try {
          const protocol = new URL(value).protocol;
          return protocol === 'http:' || protocol === 'https:';
        } catch {
          return false;
        }
      }, 'Documentation URL must use http or https')
      .optional(),
    filter: z.array(z.record(z.string(), z.unknown())).optional(),
    exclude: z.array(z.record(z.string(), z.unknown())).optional(),
    inactive: z.boolean().optional(),
    levels: z.array(trackImportLevelSchema).min(1),
  })
  .strict()
  .superRefine((track, context) => {
    const ordinals = track.levels.map(level => level.ordinal);
    if (new Set(ordinals).size !== ordinals.length) {
      context.addIssue({
        code: 'custom',
        path: ['levels'],
        message: 'Track level ordinals must be unique',
      });
    }

    const hasMandatoryCheck = track.levels.some(
      level => level.importance === 'mandatory' && level.checkIds.length > 0,
    );
    if (!hasMandatoryCheck) {
      context.addIssue({
        code: 'custom',
        path: ['levels'],
        message: 'Track must define at least one mandatory check',
      });
    }

    const checkIds = track.levels.flatMap(level => level.checkIds);
    if (new Set(checkIds).size !== checkIds.length) {
      context.addIssue({
        code: 'custom',
        path: ['levels'],
        message: 'A check cannot be assigned to multiple track levels',
      });
    }

    if (track.exclude?.length && !track.filter?.length) {
      context.addIssue({
        code: 'custom',
        path: ['exclude'],
        message: 'Exclude filters require at least one include filter',
      });
    }
  });
